import React from 'react';
import { batch } from 'react-redux';
import { isEmpty } from 'lodash';
import { recommendationsIdSelector, recommendationsTypeSelector } from 'modules/recommendations/selectors';
import { addIndication, INDICATION_TYPES } from 'modules/taboola-common-frontend-modules/Indications';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { selectedAccountSelector } from 'selectors';
import { bulkOperationsApi } from 'services/api';
import { errorMessagesUtils } from 'services/utils';
import { isNetworkAccountType } from '../../account-management';
import { duplicateCampaignError, duplicateCampaignSuccess, requestDuplicateCampaign } from '../actions';
import { fromGWtransformers, toGWtransformers } from './flowsUtils';

const successIndication = {
    message: <FormattedMessage id="campaign.duplicate.success" defaultMessage="Campaign successfully duplicated" />,
    type: INDICATION_TYPES.SUCCESS,
    highlight: <FormattedMessage id="success.highlight" defaultMessage="Success!" />,
};

const generateErrorIndication = (errorObject = {}) => ({
    message: errorMessagesUtils.getErrorMessage(errorObject),
    type: INDICATION_TYPES.ERROR,
    highlight: (
        <FormattedMessage id="campaign.duplicate.error.highlight" defaultMessage="Unable to duplicate the campaign." />
    ),
});

const generatePartialSuccessIndication = (successCount, totalCount) => ({
    message: (
        <FormattedMessage
            id="campaign.duplicate.partial.success"
            defaultMessage="Successfully duplicated {successCount} of {totalCount} campaigns"
            values={{ successCount, totalCount }}
        />
    ),
    type: INDICATION_TYPES.WARNING,
    highlight: <FormattedMessage id="partial.success.highlight" defaultMessage="Partial Success" />,
});

const duplicateCampaign =
    ({ accountId, campaignId, changes, passedDestinationAccountName, entityType }) =>
    async (dispatch, getState) => {
        const campaignChanges = toGWtransformers[entityType](changes || {});
        const { duplicateCampaignSettings = {} } = changes;
        const {
            numberOfCopies = 1,
            includeCreatives = null,
            includeBlockedPublishers = null,
            includePublisherBidModifiers = null,
            includeThirdPartyTracking = null,
        } = duplicateCampaignSettings;
        const state = getState();
        const recommendationType = recommendationsTypeSelector(state);
        const recommendationId = recommendationsIdSelector(state);
        const account = selectedAccountSelector(state);
        const { type } = account;
        const destinationAccountName = isNetworkAccountType(type) ? passedDestinationAccountName : '';

        dispatch(requestDuplicateCampaign());

        try {
            const response = await bulkOperationsApi.processCampaignOperations({
                requestBody: {
                    elements: Array.from({ length: numberOfCopies }, (_, index) => ({
                        value: {
                            ...campaignChanges,
                            accountName: accountId,
                            sourceCampaignId: campaignId,
                            recommendationType: isEmpty(recommendationType) ? null : recommendationType,
                            recommendationId: isEmpty(recommendationId) ? null : recommendationId,
                            destinationAccountId: isEmpty(destinationAccountName) ? null : destinationAccountName,
                            duplicateCampaignSettings: {
                                includeCreatives,
                                includeBlockedPublishers,
                                includePublisherBidModifiers,
                                includeThirdPartyTracking,
                            },
                            id: campaignId,
                            name: index === 0 ? campaignChanges.name : `${campaignChanges.name} (${index + 1})`,
                        },
                        error: null,
                    })),
                },
            });

            const { elements = [] } = response;
            const successfulResults = elements.filter(element => !element.error);
            const numSuccessfulResults = successfulResults.length;
            const failedResults = elements.filter(element => element.error);

            const areNoSuccessfulResults = numSuccessfulResults === 0;
            if (areNoSuccessfulResults) {
                throw failedResults[0]?.error;
            }

            const responses = successfulResults.map(element => element.value);
            const finalResponse = responses[responses.length - 1];
            const areNoFailedResults = failedResults.length === 0;

            batch(() => {
                dispatch(duplicateCampaignSuccess(finalResponse));

                if (areNoFailedResults) {
                    dispatch(addIndication(successIndication));
                    return;
                }

                dispatch(addIndication(generatePartialSuccessIndication(numSuccessfulResults, numberOfCopies)));
            });

            const transformedResponses = responses.map(response => fromGWtransformers[entityType](response));
            return transformedResponses;
        } catch (error) {
            batch(() => {
                dispatch(duplicateCampaignError(error));
                dispatch(addIndication(generateErrorIndication(error)));
            });
            throw error;
        }
    };

export default duplicateCampaign;
